import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import HintBar from '@/components/HintBar';

const HINTS = [
  'Click an activity row to highlight its particles on the rail',
  'Click a particle on the rail to inspect that sub-activity',
  'Particle size = hours per week · Color = 4B deployment',
  'Click Proceed → when ready to see opportunity scores',
];

/* ================================================================
   Deconstruct — Recipe 3.10
   3-col: Decomposition · Rails stage · Role Profile
   Interactive: click activity/sub in outline OR click a
   particle on the rails canvas → right panel updates.
   ================================================================ */

const BUCKET_VAR = {
  bot:    'var(--bot)',
  borrow: 'var(--borrow)',
  build:  'var(--build)',
  bi:     'var(--bi)',
  buy:    'var(--buy)',
};

const BUCKET_LABEL = {
  bot:    'Bot',
  borrow: 'Borrow',
  build:  'Build',
  bi:     'BI',
  buy:    'Buy',
};

const STEPS = [
  { token: '✓',  label: 'Ingest',      path: '/analysis',    done: true },
  { token: '02', label: 'Deconstruct', path: '/deconstruct', active: true },
  { token: '03', label: 'Analyze',     path: '/analyze' },
  { token: '04', label: 'Sort · 4B',   path: '/sort' },
  { token: '05', label: 'Recompose',   path: '/recompose' },
];

function readColors() {
  const cs = getComputedStyle(document.documentElement);
  const pick = (name, fallback) => (cs.getPropertyValue(name).trim() || fallback);
  return {
    bot:    pick('--bot',    '#00D4C8'),
    borrow: pick('--borrow', '#06B6D4'),
    build:  pick('--build',  '#C9A84C'),
    bi:     pick('--bi',     '#E8B96A'),
    buy:    pick('--buy',    '#EC4899'),
    text:   pick('--text',   '#FFFFFF'),
    t3:     pick('--t3',     'rgba(255,255,255,0.72)'),
    t4:     pick('--t4',     'rgba(255,255,255,0.48)'),
    line:   pick('--line',   'rgba(255,255,255,0.10)'),
  };
}

function dominantBucket(subs) {
  const counts = {};
  (subs || []).forEach((s) => {
    const k = s.deployment || 'build';
    counts[k] = (counts[k] || 0) + 1;
  });
  const sorted = Object.entries(counts).sort((a, b) => b[1] - a[1]);
  return sorted[0]?.[0] || 'build';
}

function loadRole() {
  try {
    const stored = localStorage.getItem('reai_role');
    if (stored) {
      const parsed = JSON.parse(stored);
      if (parsed?.activities?.length) return parsed;
    }
  } catch { /* ignore */ }

  try {
    const raw = localStorage.getItem('analysis_mol_results');
    if (raw) {
      const mol = JSON.parse(raw);
      const r = (mol.roles || [])[0];
      if (r) {
        const tasks = r.task_breakdown || [];
        const totalHrs = tasks.reduce((s, t) => s + (t.estimated_hours || 0), 0);
        return {
          name: r.original_title || r.role_title || 'Role',
          function: localStorage.getItem('analysis_function') || '',
          hours: Math.max(40, Math.round(totalHrs / 52) || 40),
          activities: [
            {
              name: 'All tasks',
              hours: totalHrs,
              subs: tasks.map((t) => {
                const bot = t.bot_score || t.automation_score || 0;
                const build = t.build_score || t.di_score || 0;
                return {
                  name: t.task,
                  hours: t.estimated_hours || 0,
                  deployment: t.category_4b || 'build',
                  opportunity: Math.round(Math.max(bot, build * 0.8) * 100),
                };
              }),
            },
          ],
        };
      }
    }
  } catch { /* ignore */ }

  return null;
}

const EmptyState = ({ onGo }) => (
  <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column', gap: 16, color: 'var(--text)', fontFamily: 'var(--fs)', position: 'relative', zIndex: 2 }}>
    <div className="eyebrow eye-teal">No role loaded</div>
    <p style={{ color: 'var(--t3)', fontSize: 14, maxWidth: 420, textAlign: 'center', lineHeight: 1.6 }}>
      Run the Analysis flow first, or set <code style={{ color: 'var(--teal)' }}>localStorage.reai_role</code> with
      <code style={{ color: 'var(--teal)' }}>{' {activities: [{name, hours, subs}]}'}</code>.
    </p>
    <button className="btn-teal" onClick={onGo}>Go to Analysis →</button>
  </div>
);

const Deconstruct = () => {
  const navigate = useNavigate();
  const canvasRef = useRef(null);
  const stageRef = useRef(null);
  const rafRef = useRef(null);
  const hoverRef = useRef(null);

  // Selection state
  const [activeActivity, setActiveActivity] = useState(null); // number | null
  const [activeSub, setActiveSub] = useState(null);           // { activityIdx, subIdx } | null

  // Refs shadow the state so the rAF loop always reads fresh values
  const activeActivityRef = useRef(null);
  const activeSubRef = useRef(null);
  useEffect(() => { activeActivityRef.current = activeActivity; }, [activeActivity]);
  useEffect(() => { activeSubRef.current = activeSub; }, [activeSub]);

  // Global particles → ambient
  useEffect(() => {
    const api = window.__particles;
    if (!api) return;
    api.setMode('ambient');
    return () => api.setMode('ambient');
  }, []);

  const role = useMemo(() => loadRole(), []);
  const activities = role?.activities || [];
  const totalSubs = activities.reduce((s, a) => s + (a.subs?.length || 0), 0);
  const totalHours = activities.reduce((s, a) => s + (a.hours || 0), 0);

  const bucketCounts = useMemo(() => {
    const acc = {};
    activities.forEach((a) => {
      (a.subs || []).forEach((s) => {
        const k = s.deployment || 'build';
        acc[k] = (acc[k] || 0) + 1;
      });
    });
    return acc;
  }, [activities]);
  const bucketTotal = Object.values(bucketCounts).reduce((a, b) => a + b, 0) || 1;
  const botPct = Math.round(((bucketCounts.bot || 0) / bucketTotal) * 100);

  // ── Canvas render loop ───────────────────────────
  useEffect(() => {
    const canvas = canvasRef.current;
    const stage = stageRef.current;
    if (!canvas || !stage || activities.length === 0) return;

    const ctx = canvas.getContext('2d');
    const colors = readColors();
    let W = 0, H = 0, dpr = 1;

    const measure = () => {
      dpr = Math.min(window.devicePixelRatio || 1, 2);
      const rect = stage.getBoundingClientRect();
      W = Math.max(360, rect.width);
      H = Math.max(260, rect.height);
      canvas.width = Math.floor(W * dpr);
      canvas.height = Math.floor(H * dpr);
      canvas.style.width = `${W}px`;
      canvas.style.height = `${H}px`;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    };

    const LEFT_PAD = 96;
    const RIGHT_PAD = 110;

    const railYs = () => {
      const top = 40;
      const bottom = H - 40;
      const usable = bottom - top;
      const n = activities.length;
      return activities.map((_, i) => top + (i + 0.5) * (usable / n));
    };

    // Build particle position list for hit-test & draw
    const positions = () => {
      const ys = railYs();
      const out = [];
      activities.forEach((a, ai) => {
        const y = ys[ai];
        const subs = a.subs || [];
        const railColor = colors[dominantBucket(subs)] || colors.build;
        subs.forEach((s, si) => {
          const x = LEFT_PAD + ((si + 1) / (subs.length + 1)) * (W - LEFT_PAD - RIGHT_PAD);
          const r = Math.min(11, Math.max(5, Math.sqrt(Math.max(1, s.hours || 0)) * 1.1));
          const color = colors[s.deployment] || colors.build;
          out.push({ activityIdx: ai, subIdx: si, sub: s, x, y, r, color, railColor });
        });
      });
      return out;
    };

    const hitTest = (mx, my) => {
      const pts = positions();
      let best = null;
      let bestD2 = 22 * 22;
      for (const p of pts) {
        const dx = mx - p.x, dy = my - p.y;
        const d2 = dx * dx + dy * dy;
        const hitR = Math.max(p.r + 4, 14);
        if (d2 < hitR * hitR && d2 < bestD2) {
          bestD2 = d2;
          best = p;
        }
      }
      return best;
    };

    const onMove = (e) => {
      const rect = canvas.getBoundingClientRect();
      const hit = hitTest(e.clientX - rect.left, e.clientY - rect.top);
      hoverRef.current = hit ? { activityIdx: hit.activityIdx, subIdx: hit.subIdx, x: e.clientX - rect.left, y: e.clientY - rect.top } : null;
      canvas.style.cursor = hit ? 'pointer' : 'default';
    };

    const onLeave = () => {
      hoverRef.current = null;
      canvas.style.cursor = 'default';
    };

    const onClick = (e) => {
      const rect = canvas.getBoundingClientRect();
      const hit = hitTest(e.clientX - rect.left, e.clientY - rect.top);
      if (hit) {
        setActiveSub({ activityIdx: hit.activityIdx, subIdx: hit.subIdx });
        setActiveActivity(hit.activityIdx);
      }
    };

    const draw = (now) => {
      const t = now / 1000;
      ctx.clearRect(0, 0, W, H);

      const ys = railYs();
      const activeAct = activeActivityRef.current;
      const activeS   = activeSubRef.current;
      const hover     = hoverRef.current;

      // ── Rails
      activities.forEach((a, ai) => {
        const y = ys[ai];
        const subs = a.subs || [];
        const railColor = colors[dominantBucket(subs)] || colors.build;
        const isActiveRail = activeAct == null || activeAct === ai;
        const railAlpha = isActiveRail ? 0.35 : 0.12;

        ctx.globalAlpha = railAlpha;
        ctx.strokeStyle = railColor;
        ctx.lineWidth = 1;
        ctx.setLineDash([6, 3]);
        ctx.beginPath();
        ctx.moveTo(LEFT_PAD, y);
        ctx.lineTo(W - RIGHT_PAD, y);
        ctx.stroke();
        ctx.setLineDash([]);
        ctx.globalAlpha = 1;

        // Left label — activity name
        ctx.fillStyle = isActiveRail ? railColor : colors.t4;
        ctx.font = "600 10px 'Overpass Mono', monospace";
        ctx.textAlign = 'right';
        ctx.textBaseline = 'middle';
        const label = (a.name || `Activity ${ai + 1}`).toUpperCase();
        const trimmed = label.length > 14 ? label.slice(0, 14) + '…' : label;
        ctx.fillText(trimmed, LEFT_PAD - 14, y);

        // Rail endpoint dots
        ctx.fillStyle = railColor;
        ctx.globalAlpha = isActiveRail ? 0.5 : 0.2;
        ctx.beginPath(); ctx.arc(LEFT_PAD, y, 3, 0, Math.PI * 2); ctx.fill();
        ctx.beginPath(); ctx.arc(W - RIGHT_PAD, y, 3, 0, Math.PI * 2); ctx.fill();
        ctx.globalAlpha = 1;

        // Right label — count · hours
        const hrs = subs.reduce((s, x) => s + (x.hours || 0), 0);
        ctx.fillStyle = colors.t4;
        ctx.font = "9px 'Overpass Mono', monospace";
        ctx.textAlign = 'left';
        ctx.fillText(`${subs.length} · ${hrs}h`, W - RIGHT_PAD + 12, y);
      });

      // ── Particles
      const pts = positions();
      pts.forEach((p) => {
        const isHover = hover && hover.activityIdx === p.activityIdx && hover.subIdx === p.subIdx;
        const isSel   = activeS && activeS.activityIdx === p.activityIdx && activeS.subIdx === p.subIdx;

        // Opacity rule:
        //   no selection → 1 everywhere
        //   activity selected → 1 for that activity, 0.2 for others
        //   sub selected → 1 for that sub, 0.2 for all others
        let alpha = 1;
        if (activeS) {
          alpha = isSel ? 1 : 0.2;
        } else if (activeAct != null) {
          alpha = p.activityIdx === activeAct ? 1 : 0.2;
        }

        const pulse = isHover ? 1 + Math.sin(t * 6) * 0.25 : 1;
        const r = p.r * pulse;

        // Halo
        ctx.globalAlpha = alpha * 0.4;
        ctx.fillStyle = p.color;
        ctx.beginPath();
        ctx.arc(p.x, p.y, r * 2, 0, Math.PI * 2);
        ctx.fill();

        // Dot
        ctx.globalAlpha = alpha;
        ctx.fillStyle = p.color;
        ctx.beginPath();
        ctx.arc(p.x, p.y, r, 0, Math.PI * 2);
        ctx.fill();

        // Selection ring
        if (isSel) {
          ctx.strokeStyle = p.color;
          ctx.lineWidth = 2;
          ctx.beginPath();
          ctx.arc(p.x, p.y, r + 4, 0, Math.PI * 2);
          ctx.stroke();
        }

        ctx.globalAlpha = 1;
      });

      // Hover tooltip
      if (hover) {
        const p = pts.find((q) => q.activityIdx === hover.activityIdx && q.subIdx === hover.subIdx);
        if (p) {
          const text = `${p.sub.name.length > 34 ? p.sub.name.slice(0, 34) + '…' : p.sub.name}  ·  ${p.sub.hours}h  ·  ${BUCKET_LABEL[p.sub.deployment] || p.sub.deployment}`;
          ctx.font = "11px 'Manrope', sans-serif";
          ctx.textAlign = 'left';
          ctx.textBaseline = 'middle';
          const w = ctx.measureText(text).width + 16;
          let tx = p.x + 14;
          if (tx + w > W - 8) tx = p.x - 14 - w;
          const ty = p.y - 22;
          ctx.fillStyle = 'rgba(10,10,26,0.9)';
          ctx.fillRect(tx - 4, ty - 10, w, 22);
          ctx.strokeStyle = p.color;
          ctx.lineWidth = 1;
          ctx.strokeRect(tx - 4, ty - 10, w, 22);
          ctx.fillStyle = colors.text;
          ctx.fillText(text, tx + 4, ty + 1);
        }
      }

      rafRef.current = requestAnimationFrame(draw);
    };

    const ro = new ResizeObserver(() => {
      requestAnimationFrame(() => measure());
    });
    ro.observe(stage);
    measure();
    canvas.addEventListener('mousemove', onMove);
    canvas.addEventListener('mouseleave', onLeave);
    canvas.addEventListener('click', onClick);
    rafRef.current = requestAnimationFrame(draw);

    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      ro.disconnect();
      canvas.removeEventListener('mousemove', onMove);
      canvas.removeEventListener('mouseleave', onLeave);
      canvas.removeEventListener('click', onClick);
    };
    // activities is memoized; selection comes through refs to avoid re-binding
  }, [activities]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleReset = () => {
    localStorage.removeItem('reai_role');
    navigate('/analysis');
  };

  const handleActivityClick = (ai) => {
    // Toggle off if clicking the same activity again
    setActiveActivity((cur) => (cur === ai ? null : ai));
    setActiveSub(null);
  };

  const handleSubClick = (ai, si) => {
    setActiveSub({ activityIdx: ai, subIdx: si });
    setActiveActivity(ai);
  };

  const clearSelection = () => {
    setActiveSub(null);
    setActiveActivity(null);
  };

  if (!role) return <EmptyState onGo={() => navigate('/analysis')} />;

  // Resolve the currently-selected sub + its activity for the right panel
  const selectedSub = activeSub
    ? activities[activeSub.activityIdx]?.subs?.[activeSub.subIdx]
    : null;
  const selectedActivityName = activeSub
    ? activities[activeSub.activityIdx]?.name
    : null;

  return (
    <div style={{
      height: '100vh',
      overflow: 'hidden',
      color: 'var(--text)',
      fontFamily: 'var(--fs)',
      position: 'relative',
      zIndex: 2,
      paddingTop: 'var(--nav-h)',
    }}>
      {/* ── Top nav ────────────────────────────────────── */}
      <header className="nav">
        <span className="nav-brand">re<span className="ai">AI</span>magination</span>
        <span className="nav-sep" />
        <span className="nav-lbl">Product flow</span>
        <nav className="nav-pages" style={{ marginLeft: 'auto' }}>
          <button onClick={() => navigate('/signal-room')}     className="np">Signal Room</button>
          <button onClick={() => navigate('/intelligence')}    className="np">Intelligence</button>
          <button onClick={() => navigate('/blueprint/cockpit')} className="np">Blueprint</button>
          <button onClick={() => navigate('/stage')}           className="np">Stage</button>
          <button onClick={() => navigate('/showcase/ai-tools')} className="np">Showcase</button>
        </nav>
      </header>

      {/* ── Decon chrome (5-step stepper + role chip) ──── */}
      <div className="chro-bar">
        <span className="cb-brand">re<span className="ai">AI</span>magination</span>
        <div className="cb-tabs">
          {STEPS.map((s) => (
            <button
              key={s.path}
              className={`ct${s.active ? ' on' : ''}`}
              onClick={() => !s.active && navigate(s.path)}
              style={{ opacity: s.done || s.active ? 1 : 0.6 }}
            >
              <span style={{ marginRight: 6, color: s.done ? 'var(--teal)' : 'inherit' }}>{s.token}</span>
              {s.label}
            </button>
          ))}
        </div>
        <div className="cb-right">
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 11, color: 'var(--t2)' }}>
            <span className="act-dot" style={{ background: 'var(--teal)', boxShadow: '0 0 5px var(--teal)' }} />
            {role.name}
          </span>
          <button
            onClick={handleReset}
            style={{
              padding: '4px 10px', fontSize: 10, fontFamily: 'var(--fm)',
              color: 'var(--t3)', background: 'transparent',
              border: '1px solid var(--line)', borderRadius: 'var(--r-sm)',
              cursor: 'pointer', textTransform: 'uppercase', letterSpacing: '0.12em',
            }}
          >
            Reset
          </button>
        </div>
      </div>

      <HintBar screenId="deconstruct" hints={HINTS} />

      {/* ── Main 3-col grid ──────────────────────────── */}
      <div
        className="decon-main"
        style={{
          height: 'calc(100vh - var(--nav-h) - var(--chro-h) - var(--hint-h, 0px) - var(--status-h))',
          overflow: 'hidden',
        }}
      >
        {/* Left — Decomposition outline */}
        <div className="decon-col">
          <div className="dc-hd">
            <span>Decomposition</span>
            <span>{activities.length} · {totalSubs}</span>
          </div>
          {activities.map((a, i) => {
            const bucket = dominantBucket(a.subs);
            const isActiveActivity = activeActivity === i;
            return (
              <div
                className="act"
                key={i}
                style={isActiveActivity ? {
                  background: 'var(--bg4)',
                  borderLeft: '3px solid var(--teal)',
                  paddingLeft: 11, // compensate for 3px border to avoid content shift
                } : undefined}
              >
                <div
                  className="act-name"
                  onClick={() => handleActivityClick(i)}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && handleActivityClick(i)}
                  style={{ cursor: 'pointer' }}
                >
                  <span className="act-dot" style={{ background: BUCKET_VAR[bucket], boxShadow: `0 0 5px ${BUCKET_VAR[bucket]}` }} />
                  <span>{a.name}</span>
                  <span className="act-hrs">{a.hours}h</span>
                </div>
                {(a.subs || []).map((s, j) => {
                  const isActiveSub = activeSub && activeSub.activityIdx === i && activeSub.subIdx === j;
                  const deploy = s.deployment || 'build';
                  return (
                    <div
                      className="sub"
                      key={j}
                      onClick={() => handleSubClick(i, j)}
                      role="button"
                      tabIndex={0}
                      onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && handleSubClick(i, j)}
                      style={{
                        cursor: 'pointer',
                        ...(isActiveSub ? {
                          background: 'var(--bg4)',
                          borderLeft: `3px solid var(--${deploy})`,
                          paddingLeft: 21, // sub has 24px paddingLeft; compensate 3px for border
                        } : {}),
                      }}
                    >
                      <span className="sub-n">{s.name}</span>
                      <span className="sub-h">{s.hours}h</span>
                    </div>
                  );
                })}
              </div>
            );
          })}
        </div>

        {/* Centre — rails canvas */}
        <div
          className="decon-stage"
          ref={stageRef}
          style={{
            position: 'relative',
            overflow: 'hidden',
            minHeight: 0,
            height: '100%',
            background: 'rgba(10,10,26,0.08)',
          }}
        >
          <canvas
            id="rail-cv"
            ref={canvasRef}
            style={{
              position: 'absolute',
              inset: 0,
              width: '100%',
              height: '100%',
              display: 'block',
            }}
          />
        </div>

        {/* Right — Role profile / selected sub */}
        <div className="decon-col decon-col-r">
          <div className="dc-hd">
            <span>{selectedSub ? 'Sub-activity' : 'Role Profile'}</span>
            <span>{selectedSub ? selectedActivityName : (role.function || '')}</span>
          </div>
          <div className="prof">
            {selectedSub ? (
              <>
                <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 8 }}>
                  <div>
                    <div className="prof-title">{selectedSub.name}</div>
                    <div className="prof-meta">{selectedActivityName}</div>
                  </div>
                  <button
                    onClick={clearSelection}
                    aria-label="Clear selection"
                    style={{
                      background: 'none', border: '1px solid var(--line)',
                      color: 'var(--t3)', cursor: 'pointer',
                      width: 24, height: 24, borderRadius: '50%',
                      flexShrink: 0, lineHeight: 1, padding: 0,
                      fontSize: 14, fontFamily: 'var(--fm)',
                    }}
                  >×</button>
                </div>

                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <span
                    className="act-dot"
                    style={{
                      background: `var(--${selectedSub.deployment || 'build'})`,
                      boxShadow: `0 0 5px var(--${selectedSub.deployment || 'build'})`,
                    }}
                  />
                  <span style={{ fontFamily: 'var(--fm)', fontSize: 10, color: 'var(--t3)', textTransform: 'uppercase', letterSpacing: '0.14em' }}>
                    4B · {BUCKET_LABEL[selectedSub.deployment] || selectedSub.deployment || 'build'}
                  </span>
                </div>

                <div className="pstats">
                  <div className="pstat">
                    <div className="pstat-l">Hours</div>
                    <div className="pstat-v">{selectedSub.hours || 0}</div>
                  </div>
                  <div className="pstat">
                    <div className="pstat-l">Bucket</div>
                    <div className="pstat-v" style={{ fontSize: 13 }}>
                      {(BUCKET_LABEL[selectedSub.deployment] || selectedSub.deployment || 'build').toUpperCase()}
                    </div>
                  </div>
                  <div className="pstat">
                    <div className="pstat-l">Opp.</div>
                    <div className="pstat-v">
                      {typeof selectedSub.opportunity === 'number' ? selectedSub.opportunity : '—'}
                    </div>
                  </div>
                </div>

                <button className="btn-teal" onClick={() => navigate('/analyze')} style={{ marginTop: 'auto' }}>
                  Proceed →
                </button>
              </>
            ) : (
              <>
                <div>
                  <div className="prof-title">{role.name}</div>
                  <div className="prof-meta">
                    {role.function ? `${role.function} · ` : ''}{role.hours || 40}h/week
                  </div>
                </div>

                <div className="pstats">
                  <div className="pstat">
                    <div className="pstat-l">Hours</div>
                    <div className="pstat-v">{totalHours || role.hours || 40}</div>
                  </div>
                  <div className="pstat">
                    <div className="pstat-l">Subs</div>
                    <div className="pstat-v">{totalSubs}</div>
                  </div>
                  <div className="pstat">
                    <div className="pstat-l">AI %</div>
                    <div className="pstat-v">{botPct}</div>
                  </div>
                </div>

                <div className="leg">
                  {Object.keys(BUCKET_VAR).map((k) => {
                    const n = bucketCounts[k] || 0;
                    if (!n) return null;
                    const pct = Math.round((n / bucketTotal) * 100);
                    return (
                      <div className="leg-i" key={k}>
                        <span className="leg-d" style={{ width: 8, height: 8, background: BUCKET_VAR[k] }} />
                        {BUCKET_LABEL[k]} {pct}%
                      </div>
                    );
                  })}
                </div>

                <button className="btn-teal" onClick={() => navigate('/analyze')} style={{ marginTop: 'auto' }}>
                  Proceed →
                </button>
              </>
            )}
          </div>
        </div>
      </div>

      {/* ── Status bar ───────────────────────────────── */}
      <div className="status-bar">
        <span className="st-live">● Live</span>
        <span className="st-sep">·</span>
        <span>session</span>
        <span className="st-sep">·</span>
        <span>deconstruct</span>
        <span className="st-sep">·</span>
        <span>220 · 1fr · 220</span>
        <span className="st-sep">·</span>
        <span>rails</span>
        <span className="st-sep">·</span>
        <span>
          particles · {totalSubs}
          {activeSub ? ` · 1 selected` : (activeActivity != null ? ` · ${activities[activeActivity]?.subs?.length || 0} filtered` : '')}
        </span>
      </div>
    </div>
  );
};

export default Deconstruct;
